import { ArrowLeftOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  Descriptions,
  Flex,
  Progress,
  Row,
  Skeleton,
  Space,
  Statistic,
  Table,
  Tag,
  theme,
  Tooltip,
  Typography,
} from 'antd';
import { Link, useParams, useSearchParams } from 'react-router';
import { ROLE_LABEL, type DetalheDaPessoa, type Role } from '@titularis/api-client';
import { api } from '../../api';
import { rotuloDoCanal } from '../origem';
import { emReais, formatarData, MODULO_LABEL } from '../../dominio';
import { AcoesDaConta } from './AcoesDaConta';
import { JornadaDaPessoa } from './JornadaDaPessoa';
import { rotuloDaLista, rotuloDaOrigem } from './listas';
import { RitmoDaPessoa } from './RitmoDaPessoa';
import { UltimosGestos } from './UltimosGestos';
import {
  CONTA_DE_TESTE_AJUDA,
  CONTA_DE_TESTE_COR,
  CONTA_DE_TESTE_LABEL,
  ENTRADA_LABEL,
  ETAPA_AJUDA,
  ETAPA_COR,
  ETAPA_LABEL,
  haQuantoTempo,
  PORQUE_AJUDA,
  PORQUE_COR,
  PORQUE_LABEL,
} from './vocabulario';

/**
 * A ficha de uma pessoa: tudo o que a casa sabe sobre uma conta.
 *
 * **Não existia, e é o buraco que este trabalho nasceu para tapar.** Havia como
 * listar contas, listar direitos e contar canais; não havia como abrir *uma*
 * pessoa e ver o que ela fez. A informação estava toda no banco - respostas,
 * resoluções, artigos, pedidos, inscrições - e nenhuma tela a lia.
 *
 * ## A ordem da página
 *
 * De cima para baixo, na ordem em que as perguntas aparecem:
 *
 * 1. **quem é**, com as ações ao lado, porque metade das visitas a esta página
 *    termina numa delas;
 * 2. **o que fez**, em números grandes - é a resposta à pergunta que trouxe
 *    alguém aqui;
 * 3. **onde parou**, na linha do tempo da jornada;
 * 4. **de onde veio** e **por onde entra**, que é contexto de suporte;
 * 5. **o que abriu nos últimos dias**, que é o único bloco que muda de hora em
 *    hora;
 * 6. **o que comprou e o que espera**, que é a parte comercial.
 *
 * ## O que ela deliberadamente não mostra
 *
 * **O IP e o `user_agent` das sessões.** Estão no banco e não saem da API: a
 * pergunta que a tela faz é "quando esta pessoa apareceu pela última vez", e
 * respondê-la não exige trazer o endereço de onde ela se conectou para dentro
 * de uma tela que fica aberta numa mesa. O dia em que uma investigação precisar
 * deles, eles continuam lá.
 */
export function PessoaPage() {
  const { token } = theme.useToken();
  const { id = '' } = useParams();
  const [params] = useSearchParams();

  /*
   * Por onde se voltar. A lista manda o próprio endereço no `de`, então voltar
   * devolve a página e os filtros de onde a pessoa foi aberta - e não o topo de
   * uma lista sem recorte.
   */
  const voltarPara = params.get('de');
  const destinoDeVolta = voltarPara ? `/pessoas?${voltarPara}` : '/pessoas';

  const pessoa = useQuery({
    queryKey: ['pessoa', id],
    queryFn: () => api.getPerson(id),
    enabled: Boolean(id),
  });

  if (pessoa.isPending) {
    return (
      <Card>
        <Skeleton active avatar paragraph={{ rows: 6 }} />
      </Card>
    );
  }

  if (pessoa.error || !pessoa.data) {
    return (
      <Space orientation="vertical" size={16} style={{ width: '100%' }}>
        <Link to={destinoDeVolta}>
          <Button icon={<ArrowLeftOutlined />} type="text">
            Voltar
          </Button>
        </Link>
        <Alert
          type="error"
          showIcon
          title={(pessoa.error as Error | null)?.message ?? 'Não foi possível abrir esta conta.'}
        />
      </Space>
    );
  }

  const dados = pessoa.data;

  return (
    <Space orientation="vertical" size="large" style={{ width: '100%' }}>
      <Link to={destinoDeVolta}>
        <Button icon={<ArrowLeftOutlined />} type="text" style={{ paddingInline: 0 }}>
          Voltar para a lista
        </Button>
      </Link>

      <Cabecalho dados={dados} />

      {dados.pessoa.suspensa ? (
        <Alert
          type="error"
          showIcon
          title="Conta suspensa"
          description={
            <>
              {dados.pessoa.motivoDaSuspensao ?? 'Sem motivo registrado.'}
              {dados.pessoa.suspensaAte
                ? ` · até ${formatarData(dados.pessoa.suspensaAte)}`
                : null}
            </>
          }
        />
      ) : null}

      <Numeros dados={dados} />

      <Row gutter={[token.margin, token.margin]}>
        <Col xs={24} xl={10}>
          {/*
            `minWidth: 0` nos dois lados da linha, e ele nao e decorativo: sem
            ele, um filho de largura intrinseca - o grafico do ritmo - dita a
            largura da coluna, e a pagina inteira ganha rolagem horizontal.
          */}
          <Space orientation="vertical" size={token.margin} style={{ width: '100%', minWidth: 0 }}>
            <Card size="small" title="A jornada">
              <JornadaDaPessoa jornada={dados.jornada} />
            </Card>
            <Identidade dados={dados} />
            <Origem dados={dados} />
          </Space>
        </Col>

        <Col xs={24} xl={14} style={{ minWidth: 0 }}>
          <Space orientation="vertical" size={token.margin} style={{ width: '100%', minWidth: 0 }}>
            <SemanaEAcessos dados={dados} />
            <Card size="small" title="O que abriu por último">
              <UltimosGestos gestos={dados.gestos} />
            </Card>
            <RitmoDaPessoa ritmo={dados.estudo.ritmo} />
            <Desempenho dados={dados} />
            <Comercial dados={dados} />
          </Space>
        </Col>
      </Row>
    </Space>
  );
}

function Cabecalho({ dados }: { dados: DetalheDaPessoa }) {
  const { token } = theme.useToken();
  const { pessoa } = dados;

  return (
    <Card size="small">
      <Flex justify="space-between" align="center" wrap gap={token.margin}>
        <Flex align="center" gap={token.margin}>
          <Avatar size={56} src={pessoa.foto ?? undefined}>
            {pessoa.nome.slice(0, 1).toUpperCase()}
          </Avatar>
          <Flex vertical gap={2}>
            <Flex align="center" gap={token.marginXS} wrap>
              <Typography.Title level={4} style={{ margin: 0 }}>
                {pessoa.nome}
              </Typography.Title>
              <Tooltip title={ETAPA_AJUDA[dados.etapa]}>
                <Tag color={ETAPA_COR[dados.etapa]}>{ETAPA_LABEL[dados.etapa]}</Tag>
              </Tooltip>
              {pessoa.papel !== 'user' ? (
                <Tag color="purple">{ROLE_LABEL[pessoa.papel as Role]}</Tag>
              ) : null}
              {pessoa.emailConfirmado ? null : <Tag color="orange">e-mail não confirmado</Tag>}
              {pessoa.contaDeTeste ? (
                <Tooltip title={CONTA_DE_TESTE_AJUDA}>
                  <Tag color={CONTA_DE_TESTE_COR}>{CONTA_DE_TESTE_LABEL}</Tag>
                </Tooltip>
              ) : null}
            </Flex>
            {/* Copiável porque metade do suporte começa em colar o e-mail noutro lugar. */}
            <Typography.Text type="secondary" copyable>
              {pessoa.email}
            </Typography.Text>
          </Flex>
        </Flex>

        <AcoesDaConta pessoa={{ ...pessoa }} />
      </Flex>
    </Card>
  );
}

/**
 * Os números que respondem "o que ela está fazendo", em tamanho de leitura à
 * distância. Seis, e não doze: o resto está logo abaixo, e um painel de doze
 * números pequenos não se lê, se percorre.
 */
function Numeros({ dados }: { dados: DetalheDaPessoa }) {
  const { token } = theme.useToken();
  const { estudo, compras } = dados;

  const cartoes = [
    {
      titulo: 'Respostas',
      valor: estudo.respostas,
      /*
       * As desfeitas e as questoes distintas ficam no rodape do cartao, e nao em
       * cartoes proprios: sao leitura de segunda ordem - "refez muito?",
       * "repetiu as mesmas?" - e dois cartoes a mais empurrariam para baixo o
       * que se olha primeiro.
       */
      sufixo: [
        estudo.questoes > 0 && estudo.questoes !== estudo.respostas
          ? `${estudo.questoes} questao(oes) distinta(s)`
          : null,
        estudo.desfeitas > 0 ? `${estudo.desfeitas} desfeita(s)` : null,
      ]
        .filter(Boolean)
        .join(' - ') || (null as string | null),
    },
    {
      /*
       * O percentual **já vem de 0 a 100** do `getPerformance`, que é o mesmo
       * número que o aluno vê de si. Multiplicá-lo por cem aqui produzia
       * "2500% de acerto" numa conta com uma resposta certa de quatro - visto
       * no navegador em 18/09/2026.
       */
      titulo: 'Acerto',
      valor: estudo.percentual === null ? '-' : `${estudo.percentual}%`,
      sufixo: estudo.respostas > 0 ? `${estudo.acertos} de ${estudo.respostas}` : null,
    },
    { titulo: 'Resoluções abertas', valor: estudo.resolucoes, sufixo: null },
    { titulo: 'Artigos de lei lidos', valor: estudo.artigos, sufixo: null },
    {
      titulo: 'Dias estudados',
      valor: estudo.diasEstudados,
      sufixo: `última vez ${haQuantoTempo(dados.jornada.ultimoEstudo)}`,
    },
    {
      titulo: 'Pagou',
      valor: emReais(compras.centavos),
      sufixo: compras.pedidos > 0 ? `${compras.pagos} de ${compras.pedidos} pedido(s)` : null,
    },
  ];

  return (
    <Row gutter={[token.margin, token.margin]}>
      {cartoes.map((cartao) => (
        <Col key={cartao.titulo} xs={12} sm={8} xl={4}>
          <Card size="small">
            <Statistic title={cartao.titulo} value={cartao.valor} groupSeparator="." />
            {cartao.sufixo ? (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {cartao.sufixo}
              </Typography.Text>
            ) : null}
          </Card>
        </Col>
      ))}
    </Row>
  );
}

function Identidade({ dados }: { dados: DetalheDaPessoa }) {
  const { pessoa } = dados;

  return (
    <Card size="small" title="A conta">
      <Descriptions
        column={1}
        size="small"
        items={[
          { key: 'criada', label: 'Criada em', children: formatarData(pessoa.criadaEm) },
          {
            key: 'telefone',
            label: 'Telefone',
            children: pessoa.telefone ?? <Typography.Text type="secondary">a preencher</Typography.Text>,
          },
          {
            key: 'entradas',
            label: 'Entra por',
            children:
              pessoa.entradas.length > 0 ? (
                <Space size={4} wrap>
                  {pessoa.entradas.map((entrada) => (
                    <Tag key={entrada.provider}>
                      {ENTRADA_LABEL[entrada.provider] ?? entrada.provider}
                    </Tag>
                  ))}
                </Space>
              ) : (
                <Typography.Text type="secondary">nenhuma credencial</Typography.Text>
              ),
          },
          {
            key: 'sessoes',
            label: 'Sessões',
            children: `${pessoa.sessoesAtivas} ativa(s) · último acesso ${haQuantoTempo(pessoa.ultimoAcesso)}`,
          },
          {
            key: 'focos',
            label: 'Declarou estudar',
            children:
              pessoa.focos.length > 0 ? (
                <Space size={4} wrap>
                  {pessoa.focos.map((foco) => (
                    <Tag key={foco}>
                      {MODULO_LABEL[foco as keyof typeof MODULO_LABEL] ?? foco}
                    </Tag>
                  ))}
                </Space>
              ) : (
                <Typography.Text type="secondary">não respondeu</Typography.Text>
              ),
          },
          {
            key: 'onboarding',
            label: 'Boas-vindas',
            children: pessoa.onboardingEm ? (
              `concluídas em ${formatarData(pessoa.onboardingEm)}`
            ) : (
              <Typography.Text type="secondary">
                {pessoa.dispensasDoOnboarding > 0
                  ? `dispensadas ${pessoa.dispensasDoOnboarding}x`
                  : 'ainda não viu'}
              </Typography.Text>
            ),
          },
          /*
           * Só aparece quando não é zero. Numa conta de aluno é sempre zero, e
           * cinco linhas de "0 questões cadastradas" em toda ficha empurrariam
           * para baixo o que de fato importa.
           */
          ...(dados.reportes.filed > 0
            ? [
                {
                  key: 'reportes',
                  label: 'Erros reportados',
                  children: `${dados.reportes.filed} envio(s)${
                    dados.reportes.resolved > 0 ? ` - resolveu ${dados.reportes.resolved}` : ''
                  }`,
                },
              ]
            : []),
          ...(dados.autoria.created + dados.autoria.versionsAuthored + dados.autoria.published > 0
            ? [
                {
                  key: 'autoria',
                  label: 'Escreveu para a plataforma',
                  children: `${dados.autoria.created} questão(ões) cadastrada(s) · ${dados.autoria.versionsAuthored} versão(ões) · ${dados.autoria.published} publicada(s)`,
                },
              ]
            : []),
        ]}
      />
    </Card>
  );
}

function Origem({ dados }: { dados: DetalheDaPessoa }) {
  const { origem } = dados;

  if (!origem) {
    return (
      <Card size="small" title="De onde veio">
        <Typography.Text type="secondary">
          Sem medição. É tráfego direto, ou a conta nasceu antes de 13/09/2026, quando a
          atribuição começou a ser gravada.
        </Typography.Text>
      </Card>
    );
  }

  const linhas = [
    { key: 'canal', label: 'Canal', valor: rotuloDoCanal(origem) },
    { key: 'content', label: 'Peça', valor: origem.content },
    { key: 'term', label: 'Termo', valor: origem.term },
    { key: 'referrer', label: 'Veio de', valor: origem.referrer },
    { key: 'landing', label: 'Caiu em', valor: origem.landing },
    { key: 'clickId', label: 'Id do clique', valor: origem.clickId },
    {
      key: 'firstSeenAt',
      label: 'Primeira visita',
      valor: origem.firstSeenAt ? formatarData(origem.firstSeenAt) : null,
    },
  ].filter((linha) => Boolean(linha.valor));

  return (
    <Card size="small" title="De onde veio">
      <Descriptions
        column={1}
        size="small"
        items={linhas.map((linha) => ({
          key: linha.key,
          label: linha.label,
          children: linha.valor,
        }))}
      />
    </Card>
  );
}

/**
 * A semana e o acesso, lado a lado.
 *
 * **Juntos porque são a mesma pergunta pelos dois lados**: o que esta pessoa
 * ainda pode fazer hoje. A barra de cota só quer dizer alguma coisa quando se
 * sabe se ela paga, e a lista de produtos só quer dizer alguma coisa quando se
 * sabe se ela está usando.
 */
function SemanaEAcessos({ dados }: { dados: DetalheDaPessoa }) {
  const { semana, acessos } = dados;
  const ativos = acessos.filter((acesso) => acesso.ativo);

  const barras = [
    {
      titulo: 'Questões respondidas',
      usado: semana.uso.answers,
      limite: semana.limites.freeWeeklyAnswers,
    },
    {
      titulo: 'Resoluções abertas',
      usado: semana.uso.commentaries,
      limite: semana.limites.freeWeeklyCommentaries,
    },
    {
      /*
       * **A terceira cota, que faltava.** Sem ela, cinco comentários de uma lei
       * paga na ficha de uma conta gratuita liam-se como furo de acesso - e não
       * eram: eram esta barra, cheia, fazendo o que a política manda (D-42,
       * D-54). A tela mostrava o fato e escondia a autorização.
       */
      titulo: 'Comentários de lei (degustação)',
      usado: semana.uso.libraryPages,
      limite: semana.limites.freeWeeklyLibraryReads,
    },
  ];

  /*
   * **Quem tem acesso pago não tem barra**, e é correção de uma contradição
   * vista no navegador: a barra de "3 de 3" acendia em vermelho - a cor de
   * "bateu no muro" - na ficha de alguém que havia comprado e não estava barrado
   * por nada. A cota da degustação não vale para essa conta (D-50), e desenhar
   * um limite que não limita é a tela afirmando o contrário do parágrafo logo
   * acima dela. O consumo continua à vista, em texto.
   */
  const paga = ativos.length > 0;

  return (
    <Card size="small" title="Esta semana, e o que tem acesso">
      <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
        {paga ? (
          <Space orientation="vertical" size={4} style={{ width: '100%' }}>
            <Typography.Text type="secondary">
              Tem acesso pago: a cota da degustação não a limita, e vale o teto diário, bem
              mais alto. Nesta semana respondeu {semana.uso.answers} questão(ões), abriu{' '}
              {semana.uso.commentaries} resolução(ões) e leu {semana.uso.libraryPages}{' '}
              comentário(s) de lei.
            </Typography.Text>
            <Space size={4} wrap>
              {acessos.map((acesso) => (
                <Tooltip
                  key={acesso.product}
                  title={
                    acesso.expiresAt
                      ? `até ${formatarData(acesso.expiresAt)}`
                      : 'sem prazo de validade'
                  }
                >
                  <Tag color={acesso.ativo ? 'green' : 'red'}>
                    {acesso.rotulo}
                    {acesso.ativo ? null : ' (vencido)'}
                  </Tag>
                </Tooltip>
              ))}
            </Space>
          </Space>
        ) : null}

        {(paga ? [] : barras).map((barra) => {
          const proporcao = barra.limite > 0 ? Math.min(barra.usado / barra.limite, 1) : 0;

          return (
            <Space key={barra.titulo} orientation="vertical" size={2} style={{ width: '100%' }}>
              <Flex justify="space-between">
                <Typography.Text>{barra.titulo}</Typography.Text>
                <Typography.Text type="secondary">
                  {barra.usado} de {barra.limite}
                </Typography.Text>
              </Flex>
              <Progress
                percent={Math.round(proporcao * 100)}
                showInfo={false}
                /*
                 * Cheio é laranja e não vermelho: bater no limite da degustação
                 * não é falha da pessoa nem do sistema - é o produto funcionando,
                 * e é o momento em que a oferta faz sentido.
                 */
                status={proporcao >= 1 ? 'exception' : 'normal'}
              />
            </Space>
          );
        })}

        {acessos.length === 0 ? (
          <Typography.Text type="secondary">
            Nunca teve acesso pago. Conceder um está na aba Acessos.
          </Typography.Text>
        ) : null}
      </Space>
    </Card>
  );
}

function Desempenho({ dados }: { dados: DetalheDaPessoa }) {
  const { estudo } = dados;

  const porModulo = estudo.porModulo.filter((linha) => linha.total > 0);

  if (porModulo.length === 0 && estudo.leis.length === 0) return null;

  return (
    <Card size="small" title="Em que está estudando">
      <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
        {porModulo.length > 0 ? (
          <Table
            size="small"
            rowKey="module"
            pagination={false}
            dataSource={porModulo}
            columns={[
              {
                title: 'Etapa',
                dataIndex: 'module',
                render: (valor: keyof typeof MODULO_LABEL) => MODULO_LABEL[valor] ?? valor,
              },
              { title: 'Respostas', dataIndex: 'total', align: 'right', width: 110 },
              {
                title: 'Acerto',
                key: 'percentual',
                align: 'right',
                width: 100,
                render: (_: unknown, linha) =>
                  linha.percentual === null ? '-' : `${linha.percentual}%`,
              },
            ]}
          />
        ) : null}

        {estudo.leis.length > 0 ? (
          <Table
            size="small"
            rowKey="lei"
            pagination={false}
            dataSource={estudo.leis}
            columns={[
              {
                title: 'Lei',
                key: 'lei',
                ellipsis: true,
                render: (_: unknown, linha) => (
                  <Tooltip title={linha.titulo}>{linha.lei}</Tooltip>
                ),
              },
              {
                /*
                 * **A coluna que faltava, e a falta dela custou um alarme.**
                 * Cinco artigos de uma lei paga na ficha de uma conta gratuita
                 * leem-se como furo de acesso; dizer "pela degustação" ao lado
                 * transforma a mesma linha em produto funcionando. A tela
                 * mostrava o fato e escondia a autorização.
                 */
                title: 'Como leu',
                key: 'porque',
                width: 150,
                render: (_: unknown, linha) => (
                  <Tooltip title={PORQUE_AJUDA[linha.porque]}>
                    <Tag color={PORQUE_COR[linha.porque]}>{PORQUE_LABEL[linha.porque]}</Tag>
                  </Tooltip>
                ),
              },
              { title: 'Artigos lidos', dataIndex: 'artigos', align: 'right', width: 130 },
            ]}
          />
        ) : null}
      </Space>
    </Card>
  );
}

function Comercial({ dados }: { dados: DetalheDaPessoa }) {
  const { compras, listas } = dados;

  if (compras.pedidos === 0 && listas.length === 0) return null;

  return (
    <Card size="small" title="Compras e listas">
      <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
        {compras.pedidos > 0 ? (
          <Typography.Text>
            {compras.pedidos} pedido(s), {compras.pagos} pago(s), somando{' '}
            <Typography.Text strong>{emReais(compras.centavos)}</Typography.Text>.{' '}
            {/*
              O detalhe de cada pedido continua na tela de Produtos, que é quem
              sabe conversar com o provedor. Reproduzi-lo aqui duplicaria a
              tabela e a ação de conferir no Asaas.
            */}
            <Link to={`/produtos`}>Ver os pedidos</Link>
          </Typography.Text>
        ) : null}

        {listas.length > 0 ? (
          <Space orientation="vertical" size={4} style={{ width: '100%' }}>
            <Typography.Text>Pediu para ser avisada de:</Typography.Text>
            <Space size={4} wrap>
              {listas.map((lista) => (
                <Tooltip
                  key={lista.id}
                  title={
                    lista.unsubscribedAt
                      ? `descadastrou em ${formatarData(lista.unsubscribedAt)}`
                      : `desde ${formatarData(lista.createdAt)} · origem: ${rotuloDaOrigem(lista.source)}`
                  }
                >
                  <Tag color={lista.unsubscribedAt ? 'default' : 'blue'}>
                    {rotuloDaLista(lista.topic)}
                    {lista.unsubscribedAt ? ' (saiu)' : null}
                  </Tag>
                </Tooltip>
              ))}
            </Space>
          </Space>
        ) : null}
      </Space>
    </Card>
  );
}
